/**
 * Dashboard Block Renderer — Main Dispatcher
 *
 * The single entry point for rendering any dashboard block.
 * Routes to the appropriate renderer (amCharts 5, AG Grid, or KPI React component)
 * based on block_type and returns a RenderHandle.
 */

import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { createRoot, type Root } from 'react-dom/client';
import React from 'react';

import type {
  DashboardBlockSpec,
  RenderHandle,
  RenderConfig,
  ColumnChartConfig,
  HorizontalBarConfig,
  PieDonutConfig,
  LineAreaConfig,
  DataTableConfig,
  KPICardConfig,
} from '../../../types/dashboard';

import { applyDashboardTheme } from './theme';
import { buildColumnChart } from './charts/build-column-chart';
import { buildHorizontalBarChart } from './charts/build-horizontal-bar';
import { buildPieDonutChart } from './charts/build-pie-donut-chart';
import { buildLineAreaChart } from './charts/build-line-area-chart';
import { renderAgGrid } from './table/render-ag-grid';
import { KPICard } from './kpi/KPICard';

/**
 * Render a dashboard block into a DOM container.
 *
 * Creates the appropriate visualization (amCharts 5 chart, AG Grid table,
 * or KPI card React component) and returns a RenderHandle for subsequent
 * data updates, config changes, and cleanup.
 */
export function renderBlock(
  containerId: string,
  block: DashboardBlockSpec,
  data: Record<string, unknown>[]
): RenderHandle {
  switch (block.block_type) {
    case 'clustered-column':
    case 'stacked-column':
      return renderAmChartBlock(containerId, block, data, (root, config, d) =>
        buildColumnChart(root, config as ColumnChartConfig, d, block.block_type === 'stacked-column')
      );

    case 'horizontal-bar':
      return renderAmChartBlock(containerId, block, data, (root, config, d) =>
        buildHorizontalBarChart(root, config as HorizontalBarConfig, d)
      );

    case 'pie':
    case 'donut':
      return renderAmChartBlock(containerId, block, data, (root, config, d) =>
        buildPieDonutChart(root, config as PieDonutConfig, d, block.block_type === 'donut')
      );

    case 'line':
    case 'area':
      return renderAmChartBlock(containerId, block, data, (root, config, d) =>
        buildLineAreaChart(root, config as LineAreaConfig, d, block.block_type === 'area')
      );

    case 'data-table':
      return renderAgGrid(containerId, block.id, block.render_config as DataTableConfig, data);

    case 'kpi-card':
      return renderKPICardBlock(containerId, block, data);

    default:
      throw new Error(`Unsupported block_type: "${block.block_type}"`);
  }
}

/**
 * Generic amCharts 5 block renderer.
 * Creates a root, applies the dashboard theme, delegates to a chart builder,
 * triggers the entrance animation, and returns a RenderHandle.
 */
function renderAmChartBlock(
  containerId: string,
  block: DashboardBlockSpec,
  data: Record<string, unknown>[],
  builder: (
    root: am5.Root,
    config: RenderConfig,
    data: Record<string, unknown>[]
  ) => am5.Chart
): RenderHandle {
  const root = am5.Root.new(containerId);
  applyDashboardTheme(root);

  const chart = builder(root, block.render_config, data);
  chart.appear(1000, 100);

  let currentData = data;

  return {
    blockId: block.id,
    blockType: block.block_type,
    containerId,
    update(newData: Record<string, unknown>[]) {
      currentData = newData;
      // Update series data — amCharts 5 handles animated transitions.
      // Both XYChart and PercentChart (Pie/Donut) expose `series`, but the
      // base `am5.Chart` type does not. Narrow structurally.
      const seriesContainer = (chart as { series?: am5.ListTemplate<am5.Series> }).series;
      if (seriesContainer) {
        seriesContainer.each((s) => {
          s.data.setAll(newData);
        });
      }
      // Only XY charts have axes — narrow with a downcast.
      if (chart instanceof am5xy.XYChart) {
        chart.xAxes.each((axis) => {
          if (axis.className === 'CategoryAxis') {
            axis.data.setAll(newData);
          }
        });
        chart.yAxes.each((axis) => {
          if (axis.className === 'CategoryAxis') {
            axis.data.setAll(newData);
          }
        });
      }
    },
    rerender(newConfig: RenderConfig) {
      root.dispose();
      const newBlock = { ...block, render_config: newConfig };
      const rebuilt = renderBlock(containerId, newBlock, currentData);
      this.update = rebuilt.update;
      this.rerender = rebuilt.rerender;
      this.dispose = rebuilt.dispose;
    },
    dispose() {
      root.dispose();
    },
  };
}

/**
 * KPI Card renderer using React.
 * Creates a React root, renders the KPICard component, and returns a RenderHandle
 * that re-renders on data updates.
 */
function renderKPICardBlock(
  containerId: string,
  block: DashboardBlockSpec,
  data: Record<string, unknown>[]
): RenderHandle {
  const container = document.getElementById(containerId);
  if (!container) {
    throw new Error(`Container element not found: ${containerId}`);
  }

  const reactRoot: Root = createRoot(container);
  let currentConfig = block.render_config as KPICardConfig;
  let currentData = data;

  const renderComponent = () => {
    reactRoot.render(
      React.createElement(KPICard, { config: currentConfig, data: currentData })
    );
  };

  renderComponent();

  return {
    blockId: block.id,
    blockType: 'kpi-card',
    containerId,
    update(newData: Record<string, unknown>[]) {
      currentData = newData;
      renderComponent();
    },
    rerender(newConfig: RenderConfig) {
      currentConfig = newConfig as KPICardConfig;
      renderComponent();
    },
    dispose() {
      reactRoot.unmount();
    },
  };
}
